import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { getSchedule, getMeta, getLeaders, getStandings } from "../lib/api";
import { GameCard } from "../components/GameCard";
import { FavoriteBanner } from "../components/FavoriteBanner";
import { formatKickoff } from "../lib/format";

const HERO = "https://images.unsplash.com/photo-1489944440615-453fc2b6a9a9";

const StatLeader = ({ label, player, to }) => (
  <Link
    to={to}
    data-testid={`hero-leader-${label.toLowerCase()}`}
    className="group border border-border bg-card/70 backdrop-blur p-4 hover:border-primary transition-colors duration-150"
  >
    <p className="label-caps mb-2">{label} Leader</p>
    {player && (
      <div className="flex items-center gap-3">
        <img src={player.teamLogo} alt={player.team} className="w-10 h-10 object-contain" />
        <div className="min-w-0">
          <p className="font-semibold truncate group-hover:text-primary transition-colors duration-150">
            {player.name}
          </p>
          <p className="font-display text-2xl leading-none text-primary">{player.headline}</p>
        </div>
      </div>
    )}
  </Link>
);

export default function Dashboard() {
  const { data: meta } = useQuery({ queryKey: ["meta"], queryFn: getMeta });
  const week = meta?.currentWeek;
  const { data: games = [] } = useQuery({
    queryKey: ["schedule", week],
    queryFn: () => getSchedule(week),
    enabled: !!week,
  });
  const { data: leaders } = useQuery({ queryKey: ["leaders"], queryFn: getLeaders });
  const { data: standings } = useQuery({ queryKey: ["standings"], queryFn: getStandings });

  const topTeams = standings
    ? [...Object.values(standings.AFC).flat(), ...Object.values(standings.NFC).flat()]
        .sort((a, b) => b.winPct - a.winPct)
        .slice(0, 4)
    : [];

  return (
    <div data-testid="dashboard-page" className="space-y-12">
      <FavoriteBanner />
      {/* Hero */}
      <section className="relative border border-border overflow-hidden">
        <img src={HERO} alt="stadium" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/80" />
        <div className="relative px-6 sm:px-10 py-14">
          <p className="label-caps text-primary mb-3">{meta?.season} Season · Week {week}
            {meta?.statsSeason && meta.statsSeason !== meta.season && ` · Stats from ${meta.statsSeason}`}
          </p>
          <h1 className="font-display text-6xl sm:text-8xl leading-[0.9] max-w-3xl">
            EVERY SNAP.
            <br />
            EVERY <span className="text-primary">STAT.</span>
          </h1>
          <p className="mt-4 text-muted-foreground max-w-xl">
            Live scores, full schedules, division standings and league-leading player
            stats — all in one command center.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/schedule"
              data-testid="hero-cta-schedule"
              className="px-6 py-3 bg-primary text-black font-bold uppercase tracking-wider text-sm hover:bg-white transition-colors duration-150"
            >
              View Schedule
            </Link>
            <Link
              to="/standings"
              data-testid="hero-cta-standings"
              className="px-6 py-3 border border-border font-bold uppercase tracking-wider text-sm hover:border-primary hover:text-primary transition-colors duration-150"
            >
              Standings
            </Link>
          </div>
          {leaders && (
            <div className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-3 max-w-3xl">
              <StatLeader label="Passing" player={leaders.passing?.[0]} to="/stats" />
              <StatLeader label="Rushing" player={leaders.rushing?.[0]} to="/stats" />
              <StatLeader label="Receiving" player={leaders.receiving?.[0]} to="/stats" />
            </div>
          )}
        </div>
      </section>

      {/* This week */}
      <section>
        <div className="flex items-end justify-between mb-5 border-b-2 border-border pb-2">
          <h2 className="font-display text-4xl">WEEK {week} GAMES</h2>
          <Link
            to={`/schedule?week=${week}`}
            data-testid="dashboard-view-all-games"
            className="text-sm font-semibold uppercase tracking-wider text-primary hover:text-white transition-colors duration-150"
          >
            Full Schedule →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {games.slice(0, 6).map((g) => (
            <GameCard key={g.id} game={g} />
          ))}
        </div>
      </section>

      {/* Power ranking strip */}
      <section>
        <h2 className="font-display text-4xl mb-5 border-b-2 border-border pb-2">TOP OF THE LEAGUE</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-border">
          {topTeams.map((t, i) => (
            <Link
              key={t.id}
              to={`/team/${t.id}`}
              data-testid={`top-team-${t.id}`}
              className="relative p-5 border-r border-b border-border hover:bg-white/5 transition-colors duration-150"
            >
              <span className="absolute top-3 right-4 font-display text-5xl text-border">
                {i + 1}
              </span>
              <img src={t.logo} alt={t.name} className="w-12 h-12 object-contain mb-3" />
              <p className="font-semibold">{t.fullName}</p>
              <p className="font-display text-3xl text-primary leading-none mt-1">
                {t.wins}-{t.losses}
              </p>
              <p className="label-caps mt-1">{t.streak} · {t.division}</p>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
